import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { IMAGES, PHONE_TEL, PHONE_DISPLAY } from "../constants";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[92vh] flex flex-col justify-center overflow-hidden pb-20 md:pb-28 pt-36 md:pt-44"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMAGES.hero}
          alt="Tecnico F&C Impianti al lavoro su un impianto"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#07090D] via-[#07090D]/60 to-[#07090D]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07090D]/70 to-transparent" />

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute top-24 md:top-32 right-6 md:right-12 glass rounded-full px-5 py-2.5 flex items-center gap-2"
        data-testid="hero-availability-badge"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00D2FF] opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00D2FF]" />
        </span>
        <span className="font-mono-tech text-xs uppercase tracking-[0.2em]">
          Attivi 7 giorni su 7
        </span>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono-tech text-xs md:text-sm uppercase tracking-[0.3em] text-[#00D2FF] mb-6"
          data-testid="hero-eyebrow"
        >
          Assistenza termoidraulica a domicilio
        </motion.p>

        <h1
          className="font-display font-semibold tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl"
          data-testid="hero-heading"
        >
          <MaskedLine delay={0.35}>PRONTO INTERVENTO</MaskedLine>
          <MaskedLine delay={0.5}>CALDAIE E</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="text-[#FF6B00]">CONDIZIONATORI:</span>
          </MaskedLine>
          <MaskedLine delay={0.8}>
            <span className="text-stroke">RISOLVIAMO OGGI.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#94A3B8]"
          data-testid="hero-subtitle"
        >
          Ci contatti oggi, risolviamo oggi: guasti a caldaie e condizionatori
          sistemati in giornata, in tutta Roma e provincia. Preventivo gratuito
          e tecnici certificati.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contatti"
            data-testid="hero-cta-preventivo"
            className="rounded-full bg-[#FF6B00] text-[#07090D] font-semibold px-8 py-4 text-sm uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,107,0,0.45)] active:scale-[0.98]"
          >
            Richiedi preventivo gratuito
          </Link>
          <a
            href={PHONE_TEL}
            data-testid="hero-cta-phone"
            className="flex items-center gap-3 rounded-full border-2 border-[#00D2FF] px-10 py-4 uppercase tracking-wider font-bold text-[#00D2FF] transition-all duration-300 hover:bg-[#00D2FF] hover:text-[#07090D] hover:shadow-[0_0_40px_rgba(0,210,255,0.4)] active:scale-[0.98]"
          >
            <Phone className="w-6 h-6 shrink-0" />
            <span className="flex flex-col leading-tight text-left">
              <span className="text-xs tracking-[0.15em]">Chiama ora</span>
              <span className="text-lg md:text-xl whitespace-nowrap">{PHONE_DISPLAY}</span>
            </span>
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <ArrowDown className="w-5 h-5 text-[#94A3B8] animate-bounce" />
      </motion.div>
    </section>
  );
};
